<template>
  <div class="dashboard-container">
    <!-- HEADER SECTION -->
    <b-card class="mb-3 dashboard-header" no-body>
      <b-card-body class="d-flex justify-content-between align-items-center py-2">
        <div>
          <div class="d-flex align-items-center">
            <div class="header-icon-wrapper mr-2">
              <b-icon-arrow-left-right class="header-icon" />
            </div>
            <div>
              <h5 class="mb-0">SALE VS REFUNDS ANALYTICS</h5>
              <small class="header-subtitle">Track sales performance and refund trends across all locations</small>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-badge variant="light" class="mr-2 date-badge">
            <b-icon-calendar class="mr-1" />{{ formattedDateRange }}
          </b-badge>
          <b-badge variant="primary" pill class="animated-badge" :class="getRefundRateColorClass(refundRate)">
            {{ refundRate.toFixed(1) }}% Refund Rate
          </b-badge>
        </div>
      </b-card-body>
    </b-card>

    <!-- FILTERS ROW -->
    <b-card class="mb-3 filters-card" no-body>
      <b-card-body class="py-2">
        <div class="row align-items-end">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-end" style="gap: 10px;">
              <div style="min-width: 250px;">
                <label class="small text-muted mb-1">
                  <b-icon-calendar class="mr-1" size="sm" />DATE RANGE
                </label>
                <date-picker
                  v-model="date_range"
                  format="MM-DD-YYYY"
                  valueType="format"
                  class="custom-datepicker"
                  placeholder="SELECT DATE RANGE"
                  range
                  :class="{ 'datepicker-focused': dateRangeFocused }"
                  @focus="dateRangeFocused = true"
                  @blur="dateRangeFocused = false"
                ></date-picker>
              </div>

              <div class="d-flex flex-wrap" style="gap: 5px;">
                <b-button
                  v-for="btn in quickDateButtons"
                  :key="btn.id"
                  size="sm"
                  :variant="picked_date_range === btn.id ? 'danger' : 'outline-danger'"
                  @click="set_dates(btn.id)"
                  class="quick-date-btn py-1 px-2"
                >
                  <b-icon v-if="picked_date_range === btn.id" :icon="btn.icon" class="mr-1" size="sm" />
                  {{ btn.label }}
                </b-button>
              </div>
            </div>
          </div>
          
          <div class="col-lg-4 mt-2 mt-lg-0">
            <div class="d-flex justify-content-end">
              <b-button
                variant="success"
                @click="fetch_init"
                class="search-btn"
                :disabled="items_being_loaded"
              >
                <b-icon-search class="mr-1" /> 
                <span v-if="!items_being_loaded">ANALYZE</span>
                <span v-else>
                  <b-icon-arrow-repeat spin class="mr-1" />LOADING...
                </span>
              </b-button>
            </div>
          </div>
        </div>

        <!-- ADVANCED FILTERS -->
        <div class="mt-2">
          <b-button 
            variant="link" 
            size="sm" 
            @click="showAdvancedFilters = !showAdvancedFilters"
            class="p-0 text-decoration-none"
          >
            <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1" size="sm" />
            {{ showAdvancedFilters ? 'Hide' : 'Show' }} Filters
          </b-button>
          
          <b-collapse v-model="showAdvancedFilters">
            <div class="mt-2 pt-2 border-top">
              <div class="row">
                <div class="col-md-6 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-building class="mr-1" size="sm" />LOCATION
                  </label>
                  <b-form-select
                    :options="locationOptions"
                    v-model="selected_location_id"
                    size="sm"
                    class="filter-select"
                    @change="onLocationChange"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null">ALL LOCATIONS</b-form-select-option>
                    </template>
                  </b-form-select>
                </div>

                <div class="col-md-6 mb-2">
                  <label class="small text-muted mb-1">
                    <b-icon-graph-up class="mr-1" size="sm" />CHART VIEW
                  </label>
                  <b-form-radio-group 
                    v-model="chartView" 
                    size="sm" 
                    buttons 
                    button-variant="outline-secondary"
                  >
                    <b-form-radio value="sales">Sales</b-form-radio>
                    <b-form-radio value="refunds">Refunds</b-form-radio>
                    <b-form-radio value="net">Net</b-form-radio>
                  </b-form-radio-group>
                </div>
              </div>
            </div>
          </b-collapse>
        </div>

        <!-- ACTIVE FILTERS DISPLAY -->
        <div class="mt-2 pt-2 border-top" v-if="selected_location_id">
          <div class="d-flex flex-wrap align-items-center" style="gap: 10px;">
            <span class="small text-muted">Active Filter:</span>
            <b-badge variant="primary" pill class="active-filter-badge">
              <b-icon-building class="mr-1" size="xs" />{{ getLocationName(selected_location_id) }}
              <b-icon-x class="ml-1" size="xs" @click="clearLocationFilter" style="cursor: pointer;" />
            </b-badge>
            <span class="small text-muted ml-2">Showing data for selected location only</span>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- LOADING STATE -->
    <div v-if="items_being_loaded" class="text-center py-4">
      <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
      <p class="mt-2 text-muted small">Fetching sales data...</p>
    </div>

    <!-- MAIN CONTENT -->
    <template v-else-if="items.length > 0">
      <!-- COMPACT KPI CARDS -->
      <div class="row mb-2">
        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-success">
              <b-icon-cash-stack />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Total Sales</span>
              <span class="compact-kpi-value">{{ formatCurrency(totalSales) }}</span>
              <small class="compact-kpi-sub">{{ filteredItems.length }} locations</small>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-danger">
              <b-icon-arrow-return-left />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Total Refunds</span>
              <span class="compact-kpi-value">{{ formatCurrency(totalRefunds) }}</span>
              <small :class="refundRate < 10 ? 'text-success' : 'text-danger'">{{ refundRate.toFixed(1) }}% rate</small>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-primary">
              <b-icon-wallet />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Net Revenue</span>
              <span class="compact-kpi-value">{{ formatCurrency(netRevenue) }}</span>
              <small class="compact-kpi-sub">{{ netMargin.toFixed(1) }}% margin</small>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-lg-6 col-md-6 mb-2">
          <div class="compact-kpi-card">
            <div class="compact-kpi-icon bg-warning">
              <b-icon-percent />
            </div>
            <div class="compact-kpi-info">
              <span class="compact-kpi-label">Avg Refund Rate</span>
              <span class="compact-kpi-value">{{ avgRefundRate.toFixed(1) }}%</span>
              <small class="compact-kpi-sub">per location</small>
            </div>
          </div>
        </div>
      </div>

      <!-- CHARTS SECTION -->
      <div class="row mb-2">
        <div class="col-lg-8 mb-2">
          <b-card no-body class="chart-card">
            <b-card-header class="bg-white border-bottom py-1">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="chart-header-icon mr-2">
                    <b-icon-bar-chart-line />
                  </div>
                  <h6 class="mb-0">Sales vs Refunds by Location</h6>
                </div>
                <div class="chart-view-buttons">
                  <b-button-group size="sm">
                    <b-button 
                      :variant="chartView === 'sales' ? 'success' : 'outline-secondary'"
                      @click="chartView = 'sales'"
                    >
                      <b-icon-graph-up class="mr-1" size="sm" />Sales
                    </b-button>
                    <b-button 
                      :variant="chartView === 'refunds' ? 'danger' : 'outline-secondary'"
                      @click="chartView = 'refunds'"
                    >
                      <b-icon-arrow-return-left class="mr-1" size="sm" />Refunds
                    </b-button>
                    <b-button 
                      :variant="chartView === 'net' ? 'primary' : 'outline-secondary'"
                      @click="chartView = 'net'"
                    >
                      <b-icon-cash-stack class="mr-1" size="sm" />Net
                    </b-button>
                  </b-button-group>
                </div>
              </div>
            </b-card-header>
            <b-card-body class="p-2">
              <div class="chart-container" style="height: 260px;">
                <canvas ref="salesVsRefundsChart"></canvas>
              </div>
            </b-card-body>
          </b-card>
        </div>
        <div class="col-lg-4 mb-2">
          <b-card no-body class="chart-card">
            <b-card-header class="bg-white border-bottom py-1">
              <div class="d-flex align-items-center">
                <div class="chart-header-icon mr-2 bg-danger">
                  <b-icon-exclamation-triangle />
                </div>
                <h6 class="mb-0">Refund Rate by Location</h6>
              </div>
            </b-card-header>
            <b-card-body class="p-2 d-flex justify-content-center">
              <div class="pie-chart-container" style="height: 240px;">
                <canvas ref="refundRateChart"></canvas>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </div>

      <!-- PERFORMANCE INSIGHTS -->
      <div class="row mb-2">
        <div class="col-lg-6 mb-2">
          <b-card no-body class="insight-card h-100">
            <b-card-header class="bg-white border-bottom py-1">
              <div class="d-flex align-items-center">
                <div class="insight-icon bg-success-light mr-2">
                  <b-icon-trophy />
                </div>
                <h6 class="mb-0">Top Performers</h6>
                <span class="ml-2 small text-muted">Highest Net Revenue</span>
              </div>
            </b-card-header>
            <b-card-body class="p-0">
              <div class="insight-list">
                <div v-for="(item, index) in topPerformers" :key="index" class="insight-item">
                  <div class="insight-rank" :class="getRankClass(index)">
                    {{ index + 1 }}
                  </div>
                  <div class="insight-content">
                    <div class="d-flex justify-content-between align-items-center w-100">
                      <div>
                        <h6 class="mb-0">{{ getLocationName(item.locsub) }}</h6>
                        <small class="text-muted">
                          <b-icon-percent class="mr-1" size="xs" />{{ (item.total_refunds / item.total_sale * 100).toFixed(1) }}% refund rate
                        </small>
                      </div>
                      <div class="text-right">
                        <span class="net-revenue positive">+{{ formatCurrency(item.delta) }}</span>
                        <small class="d-block text-muted">{{ formatCurrency(item.total_sale) }} sales</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="topPerformers.length === 0" class="p-4 text-center text-muted">
                  <b-icon-emoji-frown class="mb-2" font-scale="2" />
                  <p class="mb-0">No performance data available</p>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </div>

        <div class="col-lg-6 mb-2">
          <b-card no-body class="insight-card h-100">
            <b-card-header class="bg-white border-bottom py-1">
              <div class="d-flex align-items-center">
                <div class="insight-icon bg-warning-light mr-2">
                  <b-icon-exclamation-triangle />
                </div>
                <h6 class="mb-0">Refund Watchlist</h6>
                <span class="ml-2 small text-muted">Highest Refund Rates</span>
              </div>
            </b-card-header>
            <b-card-body class="p-0">
              <div class="insight-list">
                <div v-for="(item, index) in refundWatchlist" :key="index" class="insight-item">
                  <div class="insight-rank-warning" :class="getRefundRateClass(item)">
                    {{ (item.total_refunds / item.total_sale * 100).toFixed(0) }}%
                  </div>
                  <div class="insight-content">
                    <div class="d-flex justify-content-between align-items-center w-100">
                      <div>
                        <h6 class="mb-0">{{ getLocationName(item.locsub) }}</h6>
                        <small class="text-muted">
                          <b-icon-cash class="mr-1" size="xs" />{{ formatCurrency(item.total_sale) }} total
                        </small>
                      </div>
                      <div class="text-right">
                        <span class="refund-amount negative">-{{ formatCurrency(item.total_refunds) }}</span>
                        <small class="d-block text-muted">{{ ((item.total_refunds / item.total_sale * 100)).toFixed(1) }}% rate</small>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="refundWatchlist.length === 0" class="p-4 text-center text-muted">
                  <b-icon-check-circle class="mb-2" font-scale="2" variant="success" />
                  <p class="mb-0">Great! No high refund rates detected</p>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </div>
      </div>

      <!-- LOCATION PERFORMANCE TABLE -->
      <b-card no-body class="mt-2 data-table-card">
        <b-card-header class="bg-light d-flex justify-content-between align-items-center py-1">
          <div class="d-flex align-items-center">
            <b-icon-building class="mr-2" size="sm" />
            <h6 class="mb-0">Location Performance Details</h6>
            <b-badge variant="secondary" class="ml-2">{{ filteredItems.length }} locations</b-badge>
            <b-badge variant="light" class="ml-2" v-if="!selected_location_id">
              <b-icon-mouse2 size="sm" class="mr-1" />Click row to filter
            </b-badge>
            <b-badge variant="info" class="ml-2" v-else>
              <b-icon-check-circle size="sm" class="mr-1" />Filtered by location
            </b-badge>
          </div>
          <div>
            <b-button size="sm" variant="outline-secondary" @click="exportToCSV" class="mr-1">
              <b-icon-download class="mr-1" size="sm" /> Export
            </b-button>
            <b-button size="sm" variant="outline-primary" @click="fetch_init">
              <b-icon-arrow-repeat class="mr-1" size="sm" /> Refresh
            </b-button>
          </div>
        </b-card-header>
        <b-card-body class="p-0">
          <div class="table-wrapper-sm" style="max-height: 300px;">
            <table class="compact-table">
              <thead>
                <tr>
                  <th @click="sortby('locsub')">LOCATION</th>
                  <th class="text-right" @click="sortby('total_sale')">SALES ($)</th>
                  <th class="text-right" @click="sortby('total_refunds')">REFUNDS ($)</th>
                  <th class="text-right" @click="sortby('refund_rate')">REFUND RATE</th>
                  <th class="text-right" @click="sortby('delta')">NET ($)</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="item in paginatedItems" 
                  :key="item.locsub"
                  :class="{ 'highlight-row': selected_location_id == item.locsub }"
                  @click="selectLocationFromTable(item.locsub)"
                >
                  <td>{{ getLocationName(item.locsub) }}</td>
                  <td class="text-right sale-amount">{{ formatCurrency(item.total_sale || 0) }}</td>
                  <td class="text-right refund-amount">{{ formatCurrency(item.total_refunds || 0) }}</td>
                  <td class="text-right">
                    <b-badge :variant="getRefundRateVariant(item)" pill class="refund-badge">
                      {{ ((item.total_refunds / item.total_sale) * 100 || 0).toFixed(1) }}%
                    </b-badge>
                  </td>
                  <td class="text-right" :class="getDeltaClass(item)">{{ formatCurrency(item.delta || 0) }}</td>
                </tr>
              </tbody>
              <tfoot class="bg-light font-weight-bold">
                <tr>
                  <td>TOTAL</td>
                  <td class="text-right">{{ formatCurrency(totalSales) }}</td>
                  <td class="text-right text-danger">{{ formatCurrency(totalRefunds) }}</td>
                  <td class="text-right">{{ refundRate.toFixed(1) }}%</td>
                  <td class="text-right text-success">{{ formatCurrency(netRevenue) }}</td>
                </tr>
              </tfoot>
            </table>
            
            <!-- Pagination -->
            <div class="pagination-wrapper d-flex justify-content-between align-items-center p-2 bg-light">
              <small class="text-muted">
                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredItems.length) }} of {{ filteredItems.length }} locations
              </small>
              <b-pagination
                v-model="currentPage"
                :total-rows="filteredItems.length"
                :per-page="pageSize"
                size="sm"
                class="mb-0"
                align="center"
              ></b-pagination>
              <b-form-select v-model="pageSize" size="sm" style="width: 65px;" @change="currentPage = 1">
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
              </b-form-select>
            </div>
          </div>
        </b-card-body>
      </b-card>
    </template>

    <!-- NO DATA STATE -->
    <div v-else-if="!items_being_loaded && items.length === 0" class="text-center py-4">
      <b-alert show variant="light" class="mb-0">
        <b-icon-search class="mr-2" />
        Select a date range and click ANALYZE to view sales vs refunds data
      </b-alert>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import Chart from 'chart.js';
import moment from 'moment';
import 'moment/locale/en-gb';
import global_vars from '@/utilities/global_vars';
import Backer from '@/components/globals/Backer';
import Requestor from '@/utilities/Requestor';
import { 
  BIconSearch, 
  BIconGraphUp, 
  BIconArrowReturnLeft, 
  BIconCashStack,
  BIconExclamationTriangle,
  BIconCheckCircle,
  BIconDashCircle,
  BIconCalendar,
  BIconBuilding,
  BIconTable,
  BIconDownload,
  BIconArrowRepeat,
  BIconArrowUpShort,
  BIconArrowDownShort,
  BIconTrophy,
  BIconWallet,
  BIconPercent,
  BIconCash,
  BIconX,
  BIconBarChartLine,
  BIconEmojiFrown,
  BIconArrowLeftRight,
  BIconInfoCircle,
  BIconEyeSlash,
  BIconMouse2,
  BIconPeople,
  BIconTag,
  BIconPerson,
  BIconCartPlus,
  BIconClockHistory,
  BIconPieChart,
  BIconFilterCircle
} from 'bootstrap-vue';

moment.locale('en-gb'); 

export default {
  name: "ReportsRefunds",
  components: {
    Backer, 
    DatePicker,
    BIconSearch,
    BIconGraphUp,
    BIconArrowReturnLeft,
    BIconCashStack,
    BIconExclamationTriangle,
    BIconCheckCircle,
    BIconDashCircle,
    BIconCalendar,
    BIconBuilding,
    BIconTable,
    BIconDownload,
    BIconArrowRepeat,
    BIconArrowUpShort,
    BIconArrowDownShort,
    BIconTrophy,
    BIconWallet,
    BIconPercent,
    BIconCash,
    BIconX,
    BIconBarChartLine,
    BIconEmojiFrown,
    BIconArrowLeftRight,
    BIconInfoCircle,
    BIconEyeSlash,
    BIconMouse2,
    BIconPeople,
    BIconTag,
    BIconPerson,
    BIconCartPlus,
    BIconClockHistory,
    BIconPieChart,
    BIconFilterCircle
  },
  data: function(){
    return {
      packet: {},
      items: [],
      items_being_loaded: false,
      global_vars: global_vars,
      date_range: [],
      picked_date_range: null,
      sortKey: null,
      sortAsc: false,
      chartView: 'sales',
      dateRangeFocused: false,
      showAdvancedFilters: false,
      currentPage: 1,
      pageSize: 10,
      
      // Location filter
      selected_location_id: null,
      user_allowed_location_ids: [],
      user_multiple_locations_enabled: false,
      
      // Chart instances
      salesVsRefundsChart: null,
      refundRateChart: null,
      
      // Quick date buttons
      quickDateButtons: [
        { id: 'today', label: 'Today', icon: 'calendar-day' },
        { id: 'yesterday', label: 'Yesterday', icon: 'calendar-day' },
        { id: 'this_week', label: 'This Week', icon: 'calendar-week' },
        { id: 'last_week', label: 'Last Week', icon: 'calendar-week' },
        { id: 'this_month', label: 'This Month', icon: 'calendar-month' },
        { id: 'last_month', label: 'Last Month', icon: 'calendar-month' }
      ],
      
      chartColors: {
        success: '#10b981',
        danger: '#ef4444',
        primary: '#6366f1',
        warning: '#f59e0b',
        info: '#06b6d4'
      }
    }
  },
  computed: {
    filteredItems() {
      if (this.selected_location_id) {
        return this.items.filter(item => item.locsub == this.selected_location_id);
      }
      return this.items;
    },
    totalSales() {
      return this.filteredItems.reduce((sum, item) => sum + (item.total_sale || 0), 0);
    },
    totalRefunds() {
      return this.filteredItems.reduce((sum, item) => sum + (item.total_refunds || 0), 0);
    },
    netRevenue() {
      return this.totalSales - this.totalRefunds;
    },
    refundRate() {
      return this.totalSales > 0 ? (this.totalRefunds / this.totalSales * 100) : 0;
    },
    avgRefundRate() {
      if (this.filteredItems.length === 0) return 0;
      const total = this.filteredItems.reduce((sum, item) => sum + (item.total_sale > 0 ? (item.total_refunds / item.total_sale * 100) : 0), 0);
      return total / this.filteredItems.length;
    },
    netMargin() {
      return this.totalSales > 0 ? (this.netRevenue / this.totalSales * 100) : 0;
    },
    topPerformers() {
      return [...this.filteredItems]
        .filter(item => item.delta > 0)
        .sort((a, b) => b.delta - a.delta)
        .slice(0, 5);
    },
    refundWatchlist() {
      return [...this.filteredItems]
        .filter(item => item.total_sale > 0 && item.total_refunds > 0)
        .sort((a, b) => (b.total_refunds / b.total_sale) - (a.total_refunds / a.total_sale))
        .slice(0, 5);
    },
    showLocationFilter() {
      const userrole = this.packet.userDetails.userrole;
      return userrole === 'admin' || userrole === 'accountmanager' || 
             (userrole === 'locationadmin' && this.user_multiple_locations_enabled);
    },
    locationOptions() {
      let options = [{ value: null, text: 'ALL LOCATIONS' }];
      
      if (this.user_multiple_locations_enabled && this.user_allowed_location_ids.length > 0) {
        this.user_allowed_location_ids.forEach(locId => {
          const location = this.packet.locations.find(l => l.value === locId);
          if (location) {
            options.push({ value: location.value, text: location.text });
          }
        });
      } else if (this.packet.locations && this.packet.userDetails.userrole === 'admin') {
        options.push(...this.packet.locations);
      }
      
      return options;
    },
    formattedDateRange() {
      if (this.date_range && this.date_range.length === 2) {
        return `${this.date_range[0]} - ${this.date_range[1]}`;
      }
      return 'Select range';
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredItems.slice(start, end);
    }
  },
  watch: {
    filteredItems() {
      this.currentPage = 1;
      this.updateCharts();
    },
    chartView() {
      this.updateCharts();
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.loadUserLocationPermissions();
    this.initializeDateRange();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.salesVsRefundsChart) this.salesVsRefundsChart.destroy();
    if (this.refundRateChart) this.refundRateChart.destroy();
  },
  methods: {
    formatCurrency(value) {
      if (value === undefined || value === null) return '$0.00';
      return '$' + parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    initializeDateRange() {
      const today = moment();
      this.date_range = [
        today.startOf('month').format('MM-DD-YYYY'),
        today.endOf('month').format('MM-DD-YYYY')
      ];
      this.picked_date_range = 'this_month';
      this.fetch_init();
    },
    
    loadUserLocationPermissions() {
      const userrole = this.packet.userDetails.userrole;
      const properties = this.packet.userDetails.properties || {};
      
      this.user_multiple_locations_enabled = properties.multiple_locations_enabled === true;
      this.user_allowed_location_ids = properties.location_ids || [];
    },
    
    getLocationName(locsub) {
      if (!locsub) return 'Unknown';
      if (typeof locsub === 'number') {
        const location = this.packet.locations.find(l => l.value === locsub);
        return location ? location.text : locsub.toString();
      }
      return this.packet.locsubsdict[locsub] || locsub;
    },
    
    getRefundRate(item) {
      return item.total_sale > 0 ? (item.total_refunds / item.total_sale * 100) : 0;
    },
    
    getRefundRateVariant(item) {
      const rate = this.getRefundRate(item);
      if (rate > 20) return 'danger';
      if (rate > 10) return 'warning';
      return 'success';
    },
    
    getRefundRateColorClass(rate) {
      if (rate > 20) return 'badge-danger-glow';
      if (rate > 10) return 'badge-warning-glow';
      return 'badge-success-glow';
    },
    
    getRefundRateProgressClass(item) {
      const rate = this.getRefundRate(item);
      if (rate > 20) return 'bg-danger';
      if (rate > 10) return 'bg-warning';
      return 'bg-success';
    },
    
    getDeltaClass(item) {
      if (item.delta < 0) return 'negative-value';
      if (item.delta > 0) return 'positive-value';
      return '';
    },
    
    getRankClass(index) {
      if (index === 0) return 'rank-gold';
      if (index === 1) return 'rank-silver';
      if (index === 2) return 'rank-bronze';
      return '';
    },
    
    getRefundRateClass(item) {
      const rate = this.getRefundRate(item);
      if (rate > 20) return 'high-risk';
      if (rate > 10) return 'medium-risk';
      return 'low-risk';
    },
    
    calculateAvgSalesPerLocation() {
      if (this.filteredItems.length === 0) return 0;
      const avg = (this.totalSales / this.filteredItems.length) / (this.totalSales / 100);
      return Math.round(avg * 100) / 100;
    },
    
    set_dates: function(value){
      let start = null;
      let end = null;

      switch(value) {
        case 'today':
          start = moment().format("MM-DD-YYYY");
          end = moment().format("MM-DD-YYYY");
          break;
        case 'yesterday':
          start = moment().add(-1, 'days').format("MM-DD-YYYY");
          end = moment().add(-1, 'days').format("MM-DD-YYYY");
          break;
        case 'this_week':
          start = moment().startOf('week').format("MM-DD-YYYY");
          end = moment().endOf('week').format("MM-DD-YYYY");
          break;
        case 'last_week':
          start = moment().subtract(1, 'weeks').startOf('week').format("MM-DD-YYYY");
          end = moment().subtract(1, 'weeks').endOf('week').format("MM-DD-YYYY");
          break;
        case 'this_month':
          start = moment().startOf('month').format("MM-DD-YYYY");
          end = moment().endOf('month').format("MM-DD-YYYY");
          break;
        case 'last_month':
          start = moment().subtract(1, 'months').startOf('month').format("MM-DD-YYYY");
          end = moment().subtract(1, 'months').endOf('month').format("MM-DD-YYYY");
          break;
      }

      if (start && end) {
        this.date_range = [start , end];
        this.picked_date_range = value;
        this.fetch_init();
      }
    },
    
    onLocationChange() {
      this.currentPage = 1;
      this.fetch_init();
    },
    
    clearLocationFilter() {
      this.selected_location_id = null;
      this.currentPage = 1;
      this.fetch_init();
    },
    
    selectLocationFromTable(locationId) {
      if (this.selected_location_id === locationId) {
        // If already selected, clear the filter
        this.selected_location_id = null;
      } else {
        // Select the location
        this.selected_location_id = locationId;
      }
      this.currentPage = 1;
    },
    
    fetch_init: function(){
      let dates = this.date_range;
      let that = this;
      let data = {dates: dates, username: this.packet.userDetails.username}
      
      if (this.selected_location_id) {
        data.location_id = this.selected_location_id;
      }
      
      let path = "/api/reports/reports_v4/"
      let s = function(response) {
        if (response.data.result){
          that.items = response.data.result.map(item => ({
            ...item,
            refund_rate: item.total_sale > 0 ? (item.total_refunds / item.total_sale * 100) : 0
          }));
        } else {
          that.$bvToast.toast(
            response.data.error || "No data returned", 
            {title: "Error", autoHideDelay: 1500, variant:"warning"}
          );
        }
        that.items_being_loaded = false
      }

      let f = function(error) {
        console.log(error)
        that.$bvToast.toast(
          "Error occurred. Please try again.",
          {title: "Error", autoHideDelay: 1500, variant:"danger"}
        );
        that.items_being_loaded = false
      }
      this.items_being_loaded = true;
      Requestor.postSyncP(path, data, this, s, f);
    },
    
    sortby: function(key){
      this.sortKey = key;
      
      if (key === 'refund_rate') {
        this.items.sort((a, b) => {
          const rateA = a.total_sale > 0 ? (a.total_refunds / a.total_sale * 100) : 0;
          const rateB = b.total_sale > 0 ? (b.total_refunds / b.total_sale * 100) : 0;
          return this.sortAsc ? rateA - rateB : rateB - rateA;
        });
      } else {
        if (this.sortAsc){
          this.items = Requestor.sort(this.items, key)
        } else {
          this.items = Requestor.sort_reverse(this.items, key)
        }
      }
      this.sortAsc = !this.sortAsc;
      this.currentPage = 1;
    },
    
    exportToCSV() {
      const headers = ['Location', 'Total Sales', 'Total Refunds', 'Refund Rate (%)', 'Net Revenue'];
      const rows = this.filteredItems.map(item => [
        this.getLocationName(item.locsub),
        item.total_sale || 0,
        item.total_refunds || 0,
        ((item.total_refunds / item.total_sale) * 100 || 0).toFixed(1),
        item.delta || 0
      ]);
      
      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_vs_refunds_${moment().format('YYYY-MM-DD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      this.$bvToast.toast('Export completed!', { title: 'Success', autoHideDelay: 1500, variant: 'success' });
    },
    
    handleResize() {
      this.updateCharts();
    },
    
    updateCharts() {
      this.$nextTick(() => {
        this.renderSalesVsRefundsChart();
        this.renderRefundRateChart();
      });
    },
    
    renderSalesVsRefundsChart() {
      const ctx = this.$refs.salesVsRefundsChart;
      if (!ctx || this.filteredItems.length === 0) return;
      
      if (this.salesVsRefundsChart) {
        this.salesVsRefundsChart.destroy();
      }
      
      const sortedItems = [...this.filteredItems].sort((a, b) => {
        switch(this.chartView) {
          case 'sales': return b.total_sale - a.total_sale;
          case 'refunds': return b.total_refunds - a.total_refunds;
          case 'net': return b.delta - a.delta;
          default: return b.total_sale - a.total_sale;
        }
      });
      
      const labels = sortedItems.map(item => {
        const name = this.getLocationName(item.locsub);
        return name.length > 15 ? name.substring(0, 12) + '...' : name;
      });
      const salesData = sortedItems.map(item => item.total_sale || 0);
      const refundsData = sortedItems.map(item => item.total_refunds || 0);
      const netData = sortedItems.map(item => item.delta || 0);
      
      let datasets = [];
      
      switch(this.chartView) {
        case 'sales':
          datasets = [
            {
              label: 'Sales',
              data: salesData,
              backgroundColor: 'rgba(16, 185, 129, 0.8)',
              borderColor: '#10b981',
              borderWidth: 1,
              borderRadius: 4,
              barPercentage: 0.6
            },
            {
              label: 'Refunds',
              data: refundsData,
              backgroundColor: 'rgba(239, 68, 68, 0.8)',
              borderColor: '#ef4444',
              borderWidth: 1,
              borderRadius: 4,
              barPercentage: 0.6
            }
          ];
          break;
        case 'refunds':
          datasets = [
            {
              label: 'Refunds',
              data: refundsData,
              backgroundColor: 'rgba(239, 68, 68, 0.8)',
              borderColor: '#ef4444',
              borderWidth: 1,
              borderRadius: 4,
              barPercentage: 0.6
            },
            {
              label: 'Sales',
              data: salesData,
              backgroundColor: 'rgba(16, 185, 129, 0.5)',
              borderColor: '#10b981',
              borderWidth: 1,
              borderRadius: 4,
              barPercentage: 0.6
            }
          ];
          break;
        case 'net':
          datasets = [
            {
              label: 'Net Revenue',
              data: netData,
              backgroundColor: netData.map(value => value >= 0 ? 'rgba(99, 102, 241, 0.8)' : 'rgba(239, 68, 68, 0.8)'),
              borderColor: netData.map(value => value >= 0 ? '#6366f1' : '#ef4444'),
              borderWidth: 1,
              borderRadius: 4,
              barPercentage: 0.6
            }
          ];
          break;
      }
      
      this.salesVsRefundsChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: { usePointStyle: true, boxWidth: 10, font: { size: 10 } }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) label += ': ';
                  label += '$' + context.parsed.y.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  });
                  return label;
                }
              }
            }
          },
          scales: {
            x: {
              ticks: { maxRotation: 45, minRotation: 45, font: { size: 10 } },
              grid: { display: false }
            },
            y: {
              beginAtZero: true,
              grid: { color: 'rgba(0, 0, 0, 0.05)' },
              ticks: {
                callback: function(value) {
                  return '$' + value.toLocaleString();
                }
              }
            }
          }
        }
      });
    },
    
    renderRefundRateChart() {
      const ctx = this.$refs.refundRateChart;
      if (!ctx || this.filteredItems.length === 0) return;
      
      if (this.refundRateChart) {
        this.refundRateChart.destroy();
      }
      
      const validItems = this.filteredItems.filter(item => item.total_sale > 0);
      const sortedItems = [...validItems]
        .sort((a, b) => (b.total_refunds / b.total_sale) - (a.total_refunds / a.total_sale))
        .slice(0, 10);
      
      const labels = sortedItems.map(item => {
        const name = this.getLocationName(item.locsub);
        return name.length > 12 ? name.substring(0, 10) + '...' : name;
      });
      const refundRates = sortedItems.map(item => (item.total_refunds / item.total_sale * 100));
      
      const backgroundColors = refundRates.map(rate => {
        if (rate > 20) return '#ef4444';
        if (rate > 10) return '#f59e0b';
        return '#10b981';
      });
      
      this.refundRateChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: labels,
          datasets: [{
            data: refundRates,
            backgroundColor: backgroundColors,
            borderWidth: 2,
            borderColor: '#fff',
            hoverOffset: 8
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '60%',
          plugins: {
            legend: {
              position: 'right',
              labels: { usePointStyle: true, boxWidth: 8, font: { size: 9 } }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const item = sortedItems[context.dataIndex];
                  return [
                    `${context.label}: ${context.raw.toFixed(1)}% refund rate`,
                    `Sales: $${item.total_sale.toLocaleString()}`,
                    `Refunds: $${item.total_refunds.toLocaleString()}`,
                    `Net: $${item.delta.toLocaleString()}`
                  ];
                }
              }
            }
          }
        }
      });
    }
  },
  created: function(){
    const storedPacket = localStorage.getItem('packet');
    const storedUserDetails = localStorage.getItem('userDetails');
    
    this.packet = storedPacket ? JSON.parse(storedPacket) : {};
    this.packet.userDetails = storedUserDetails ? JSON.parse(storedUserDetails) : {};
    
    if (!this.packet.locsubsdict) {
      this.packet.locsubsdict = {};
    }
    if (!this.packet.locations) {
      this.packet.locations = [];
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
  padding: 12px;
}

/* Header Styles */
.dashboard-header {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: white;
  border: none;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.header-icon-wrapper {
  width: 36px;
  height: 36px;
  background: rgba(255,255,255,0.15);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon { font-size: 18px; animation: pulse 2s infinite; }
@keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }

.dashboard-header h5 {
  color: white;
  font-weight: 600;
}

.header-subtitle {
  color: rgba(255,255,255,0.8);
}

.date-badge {
  background-color: rgba(255,255,255,0.15);
  color: white;
  border: none;
  padding: 2px 10px;
  border-radius: 14px;
  font-size: 11px;
}

.animated-badge {
  animation: fadeIn 0.5s ease-out;
}

.badge-danger-glow {
  background: #ef4444;
  box-shadow: 0 0 10px rgba(239,68,68,0.5);
}

.badge-warning-glow {
  background: #f59e0b;
  box-shadow: 0 0 10px rgba(245,158,11,0.5);
}

.badge-success-glow {
  background: #10b981;
  box-shadow: 0 0 10px rgba(16,185,129,0.5);
}

/* Filters Card */
.filters-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.05);
  background: white;
}

.custom-datepicker {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 12px;
  width: 100%;
}

.datepicker-focused {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99,102,241,0.1);
}

.quick-date-btn {
  border-radius: 14px;
  font-size: 11px;
  padding: 3px 8px;
}

.filter-select {
  border-radius: 7px;
  background: #f8fafc;
  border-color: #e2e8f0;
  font-size: 12px;
}

.search-btn {
  border-radius: 8px;
  padding: 4px 20px;
  font-weight: 600;
  font-size: 12px;
}

.active-filter-badge {
  cursor: default;
  background: #e2e8f0;
  color: #1e293b;
}

/* Compact KPI Cards */
.compact-kpi-card {
  background: white;
  border-radius: 10px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  transition: all 0.2s;
}

.compact-kpi-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0,0,0,0.1);
}

.compact-kpi-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.bg-primary { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.bg-success { background: linear-gradient(135deg, #10b981, #34d399); }
.bg-danger { background: linear-gradient(135deg, #ef4444, #f87171); }
.bg-warning { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.bg-purple { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }

.compact-kpi-info {
  display: flex;
  flex-direction: column;
}

.compact-kpi-label {
  font-size: 10px;
  color: #64748b;
  display: block;
}

.compact-kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  display: block;
  line-height: 1.2;
}

.compact-kpi-sub {
  font-size: 9px;
  color: #94a3b8;
}

/* Chart Cards */
.chart-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
}

.chart-header-icon {
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

.chart-header-icon.bg-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.chart-container, .pie-chart-container {
  position: relative;
  width: 100%;
}

.chart-view-buttons {
  display: flex;
  gap: 4px;
}

/* Insight Cards */
.insight-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
}

.insight-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.bg-success-light {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.bg-warning-light {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.insight-list {
  max-height: 280px;
  overflow-y: auto;
}

.insight-item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.insight-item:hover {
  background: #f8fafc;
}

.insight-rank, .insight-rank-warning {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 10px;
  flex-shrink: 0;
}

.insight-rank {
  background: #f1f5f9;
  color: #64748b;
}

.rank-gold {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: white;
}

.rank-silver {
  background: linear-gradient(135deg, #94a3b8, #64748b);
  color: white;
}

.rank-bronze {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: white;
}

.insight-rank-warning {
  font-size: 12px;
  font-weight: bold;
}

.high-risk {
  background: #fee2e2;
  color: #ef4444;
}

.medium-risk {
  background: #fed7aa;
  color: #f59e0b;
}

.low-risk {
  background: #d1fae5;
  color: #10b981;
}

.insight-content {
  flex: 1;
}

.net-revenue.positive, .positive-value {
  color: #10b981;
  font-weight: 600;
}

.refund-amount.negative, .negative-value {
  color: #ef4444;
  font-weight: 600;
}

/* Data Tables */
.data-table-card {
  border: none;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
  max-height: 400px;
}

.table-wrapper-sm {
  overflow-x: auto;
}

.compact-table, .sales-table {
  width: 100%;
  font-size: 11px;
  border-collapse: collapse;
}

.compact-table th, .compact-table td,
.sales-table th, .sales-table td {
  padding: 6px 8px;
  border: 1px solid #dee2e6;
}

.compact-table th, .sales-table th {
  background: #c3cdd3;
  font-weight: 600;
  position: sticky;
  top: 0;
  white-space: nowrap;
  cursor: pointer;
}

.sales-table td { white-space: nowrap; }

.compact-table tbody tr, .sales-table tbody tr {
  cursor: pointer;
  transition: background 0.2s;
}

.compact-table tbody tr:hover, .sales-table tbody tr:hover {
  background: #e0e7ff !important;
}

.highlight-row {
  background: #dbeafe !important;
  border-left: 3px solid #6366f1;
}

.sale-amount {
  font-weight: 500;
}

.refund-amount {
  color: #ef4444;
}

.refund-badge {
  font-size: 10px;
  padding: 2px 6px;
}

.pagination-wrapper {
  border-top: 1px solid #e2e8f0;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Scrollbar Styling */
.insight-list::-webkit-scrollbar,
.table-wrapper::-webkit-scrollbar,
.table-wrapper-sm::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.insight-list::-webkit-scrollbar-track,
.table-wrapper::-webkit-scrollbar-track,
.table-wrapper-sm::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.insight-list::-webkit-scrollbar-thumb,
.table-wrapper::-webkit-scrollbar-thumb,
.table-wrapper-sm::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.insight-list::-webkit-scrollbar-thumb:hover,
.table-wrapper::-webkit-scrollbar-thumb:hover,
.table-wrapper-sm::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-container { padding: 8px; }
  .compact-kpi-value { font-size: 13px; }
  .compact-table th, .compact-table td,
  .sales-table th, .sales-table td { padding: 4px 6px; font-size: 9px; }
  .compact-kpi-icon { width: 32px; height: 32px; font-size: 14px; }
  .dashboard-header h5 { font-size: 14px; }
  .header-subtitle { font-size: 10px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsRefunds.vue